import { getExperiment } from 'actions/SystemActions'
import config from 'constants/config'
import { useEffect } from 'react'
import type { AppDispatch } from 'src/client/store'
import { useDispatch, useSelector } from 'react-redux'
import { isCustomerSupportMode } from 'selectors/featuresSelectors'
import { isLereSerendipityEnabled } from 'lib/recommendationUtils'
import { EXPERIMENT, setExperimentValue, startExperiment } from 'lib/optimize'
import setServerExperimentCookie from 'cookies/setServerExperimentCookie'

let hasRun = false

function useOptimizeExperiments() {
  const dispatch = useDispatch<AppDispatch>()
  const csMode = useSelector(isCustomerSupportMode)
  const experiments = useSelector((state: App.State) => state.config.experiments)

  useEffect(() => {
    if (hasRun) {
      return
    }

    hasRun = true

    // put your optimize experiment gets here, e.g.
    // if (config.<experiment env var>) {
    //   dispatch(getExperiment(config.<experiment env var>))
    // }

    if (config.AB_TEST_UNIVERSAL_CHECKOUT_STANDALONE_FLIGHTS_ID && !csMode) {
      dispatch(getExperiment(config.AB_TEST_UNIVERSAL_CHECKOUT_STANDALONE_FLIGHTS_ID))
    }

    if (config.AB_TEST_RESERVE_FOR_ZERO && !csMode) {
      dispatch(getExperiment(config.AB_TEST_RESERVE_FOR_ZERO))
      startExperiment(EXPERIMENT.AB_TEST_RESERVE_FOR_ZERO)
    }

    if (config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_ENABLE_CHALLENGER_EXPERIMENT_ID))
    }

    if (config.AB_TEST_TOURS_ALTERNATING_LE_AND_TTC_OFFER_LIST_EXPERIMENT_ID) {
      dispatch(getExperiment(config.AB_TEST_TOURS_ALTERNATING_LE_AND_TTC_OFFER_LIST_EXPERIMENT_ID))
    }

    if (config.AB_TEST_SEMI_LOGGED_IN) {
      dispatch(getExperiment(config.AB_TEST_SEMI_LOGGED_IN))
    }

    if (isLereSerendipityEnabled() && config.LERE_SERENDIPITY_TILES_VARIANT_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_SERENDIPITY_TILES_VARIANT_EXPERIMENT_ID))
    }

    if (config.LERE_IN_CONTENT_YMAL_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_IN_CONTENT_YMAL_EXPERIMENT_ID))
    }

    if (config.AB_TEST_NEW_BRAND_BUTTON_COLOURS) {
      dispatch(getExperiment(config.AB_TEST_NEW_BRAND_BUTTON_COLOURS))
    }

    if (config.AB_TEST_SEARCH_PAGES_TITLE_EXPERIMENT_ID) {
      dispatch(getExperiment(config.AB_TEST_SEARCH_PAGES_TITLE_EXPERIMENT_ID))
    }

    if (config.LERE_LOCATION_FLEX_SERENDIPITY_VARIANT_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_LOCATION_FLEX_SERENDIPITY_VARIANT_EXPERIMENT_ID))
    }

    if (config.AB_TEST_SEARCH_SORT_BY_EXPECTED_VALUE) {
      dispatch(getExperiment(config.AB_TEST_SEARCH_SORT_BY_EXPECTED_VALUE))
    }

    if (config.AB_TEST_INSURANCE_UNSELECTED_BY_DEFAULT) {
      dispatch(getExperiment(config.AB_TEST_INSURANCE_UNSELECTED_BY_DEFAULT))
    }

    if (config.LERE_ENABLE_NON_LPP_OFFER_STICKY_FOOTER_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_ENABLE_NON_LPP_OFFER_STICKY_FOOTER_EXPERIMENT_ID))
    }

    if (config.LERE_ENABLE_EXPIRED_DEALS_STICKY_FOOTER_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_ENABLE_EXPIRED_DEALS_STICKY_FOOTER_EXPERIMENT_ID))
    }

    if (config.LERE_HOTEL_SEARCH_PAGE_STICKY_FOOTER_ON_SCROLL_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_HOTEL_SEARCH_PAGE_STICKY_FOOTER_ON_SCROLL_EXPERIMENT_ID))
    }

    if (config.LERE_HOTEL_SEARCH_PAGE_STICKY_FOOTER_ON_IDLE_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_HOTEL_SEARCH_PAGE_STICKY_FOOTER_ON_IDLE_EXPERIMENT_ID))
    }

    if (config.AB_TEST_EXPERIENCE_ONLY_INFLOW_TRANSFERS) {
      dispatch(getExperiment(config.AB_TEST_EXPERIENCE_ONLY_INFLOW_TRANSFERS))
    }

    if (config.PAYMENTS_INSTALMENTS_EXPERIMENT_ID) {
      dispatch(getExperiment(config.PAYMENTS_INSTALMENTS_EXPERIMENT_ID))
    }

    if (config.AB_TEST_TOURS_PRICE_PER_DAY) {
      dispatch(getExperiment(config.AB_TEST_TOURS_PRICE_PER_DAY))
    }

    if (config.AB_TEST_NEW_OFFER_TILE_AND_LANDING_PAGE) {
      dispatch(getExperiment(config.AB_TEST_NEW_OFFER_TILE_AND_LANDING_PAGE))
    }

    if (config.AB_TEST_SKIP_EXPERIENCES_EXPERIMENT_ID) {
      dispatch(getExperiment(config.AB_TEST_SKIP_EXPERIENCES_EXPERIMENT_ID))
    }

    if (config.AB_TEST_CRUISE_SUPPORT_BANNER_EXPERIMENT_ID) {
      dispatch(getExperiment(config.AB_TEST_CRUISE_SUPPORT_BANNER_EXPERIMENT_ID))
    }

    if (config.AB_TEST_PEOPLE_LIKE_ME_ENTRY) {
      dispatch(getExperiment(config.AB_TEST_PEOPLE_LIKE_ME_ENTRY))
    }

    if (config.AB_TEST_SHOW_ON_MAP) {
      dispatch(getExperiment(config.AB_TEST_SHOW_ON_MAP))
    }

    if (config.AB_TEST_ANYWHERE_FILTERS_ON_HOTELS_PAGE) {
      dispatch(getExperiment(config.AB_TEST_ANYWHERE_FILTERS_ON_HOTELS_PAGE))
    }

    if (config.AB_TEST_NEW_HOTEL_SEARCH_PAGE_AU) {
      dispatch(getExperiment(config.AB_TEST_NEW_HOTEL_SEARCH_PAGE_AU))
    }

    if (config.AB_TEST_NEW_HOTEL_SEARCH_PAGE_NON_AU) {
      dispatch(getExperiment(config.AB_TEST_NEW_HOTEL_SEARCH_PAGE_NON_AU))
    }

    if (config.AB_TEST_MAP_POSITION) {
      dispatch(getExperiment(config.AB_TEST_MAP_POSITION))
    }

    if (config.AB_TEST_TRENDING_DESTINATIONS_BY_PERCENTILE) {
      dispatch(getExperiment(config.AB_TEST_TRENDING_DESTINATIONS_BY_PERCENTILE))
    }

    if (config.AB_TEST_PROPERTY_SEARCH_TO_OFFER_PAGE) {
      dispatch(getExperiment(config.AB_TEST_PROPERTY_SEARCH_TO_OFFER_PAGE))
    }

    if (config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID) {
      dispatch(getExperiment(config.LERE_ENABLE_PERSONALISED_ABANDONED_CARTS_EXPERIMENT_ID))
    }

    if (config.AB_TEST_RESERVE_VS_PAY_WORDING_FOR_DEPOSITS_EXPERIMENT_ID) {
      dispatch(getExperiment(config.AB_TEST_RESERVE_VS_PAY_WORDING_FOR_DEPOSITS_EXPERIMENT_ID))
    }

    if (config.AB_TEST_CURATED_TRIPS_OFFER_PAGE_PROMOTION) {
      dispatch(getExperiment(config.AB_TEST_CURATED_TRIPS_OFFER_PAGE_PROMOTION))
    }

    if (config.AB_TEST_CURATED_TRIPS_HOTEL_SEARCH_PAGE_PROMOTION) {
      dispatch(getExperiment(config.AB_TEST_CURATED_TRIPS_HOTEL_SEARCH_PAGE_PROMOTION))
    }

    if (config.AB_TEST_USER_REVIEWS) {
      dispatch(getExperiment(config.AB_TEST_USER_REVIEWS))
    }

    if (config.AB_TEST_TRENDING_LOCATIONS_COPY) {
      dispatch(getExperiment(config.AB_TEST_TRENDING_LOCATIONS_COPY))
    }

    if (config.AB_TEST_UK_SIGN_UP) {
      dispatch(getExperiment(config.AB_TEST_UK_SIGN_UP))
    }

    if (config.AB_TEST_HOME_PAGE_LOADED) {
      dispatch(getExperiment(config.AB_TEST_HOME_PAGE_LOADED))
    }

    if (config.AB_TEST_OFFER_PAGE_LOADED) {
      dispatch(getExperiment(config.AB_TEST_OFFER_PAGE_LOADED))
    }

    if (config.AB_TEST_CHECKOUT_PAGE_LOADED) {
      dispatch(getExperiment(config.AB_TEST_CHECKOUT_PAGE_LOADED))
    }

    if (config.AB_TEST_REMOVE_NORTON_BADGE_AU) {
      dispatch(getExperiment(config.AB_TEST_REMOVE_NORTON_BADGE_AU))
    }

    if (config.AB_TEST_REMOVE_NORTON_BADGE_US) {
      dispatch(getExperiment(config.AB_TEST_REMOVE_NORTON_BADGE_US))
    }

    if (config.AB_TEST_REMOVE_NORTON_BADGE_UK) {
      dispatch(getExperiment(config.AB_TEST_REMOVE_NORTON_BADGE_UK))
    }

    if (config.AB_TEST_NEW_URGENCY_ON_OFFER_PAGES) {
      dispatch(getExperiment(config.AB_TEST_NEW_URGENCY_ON_OFFER_PAGES))
    }

    if (config.AB_TEST_PAY_BUTTON_COPY) {
      dispatch(getExperiment(config.AB_TEST_PAY_BUTTON_COPY))
    }

    if (config.AB_TEST_UK_SIGN_UP_TIMER_DESKTOP) {
      dispatch(getExperiment(config.AB_TEST_UK_SIGN_UP_TIMER_DESKTOP))
    }

    if (config.AB_TEST_UK_SIGN_UP_TIMER_MOBILE) {
      dispatch(getExperiment(config.AB_TEST_UK_SIGN_UP_TIMER_MOBILE))
    }

    if (config.AB_TEST_UK_ATOL_BANNER_DESKTOP) {
      dispatch(getExperiment(config.AB_TEST_UK_ATOL_BANNER_DESKTOP))
    }

    if (config.AB_TEST_UK_ATOL_BANNER_MOBILE) {
      dispatch(getExperiment(config.AB_TEST_UK_ATOL_BANNER_MOBILE))
    }

    // START OF SERVER SIDE EXPERIMENTS
    // Initialize them @ src/server/state/serverExperiment.ts

    // This is an example of how to initialise an SSR ab test

    // get setExperimentValue from 'lib/optimize'
    // get setServerExperimentCookie from 'cookies/setServerExperimentCookie'

    // const ssrTestVariant = experiments[config.<experiment env var>]
    // if (config.<experiment env var> && typeof ssrTestVariant !== 'undefined') {
    //   setExperimentValue(config.<experiment env var>, ssrTestVariant)
    //   setServerExperimentCookie(config.<experiment env var>, ssrTestVariant)
    // }

    const scooponHomepage = experiments[config.AB_SCOOPON_ACCOMMODATION_HOMEPAGE_EXPERIMENT_ID]
    if (config.AB_SCOOPON_ACCOMMODATION_HOMEPAGE_EXPERIMENT_ID && config.BRAND === 'scoopontravel') {
      setExperimentValue(config.AB_SCOOPON_ACCOMMODATION_HOMEPAGE_EXPERIMENT_ID, scooponHomepage)
      setServerExperimentCookie(config.AB_SCOOPON_ACCOMMODATION_HOMEPAGE_EXPERIMENT_ID, scooponHomepage)
    }

    const newHomePage = experiments[config.AB_TEST_NEW_HOME_PAGE]
    if (config.AB_TEST_NEW_HOME_PAGE) {
      setExperimentValue(config.AB_TEST_NEW_HOME_PAGE, newHomePage)
      setServerExperimentCookie(config.AB_TEST_NEW_HOME_PAGE, newHomePage)
    }

    const newHomePageMobile = experiments[config.AB_TEST_NEW_HOME_PAGE_MOBILE]
    if (config.AB_TEST_NEW_HOME_PAGE_MOBILE) {
      setExperimentValue(config.AB_TEST_NEW_HOME_PAGE_MOBILE, newHomePageMobile)
      setServerExperimentCookie(config.AB_TEST_NEW_HOME_PAGE_MOBILE, newHomePageMobile)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export default useOptimizeExperiments
